import { createAction, props } from '@ngrx/store'
import { UserModel, Credentials, UserRolesModel, RequestModel, ResellerModel } from '@models/index'

/**
 * Creates the action getReseller
 * that searches the reseller according to the 
 * browser url.
 * Receives a {@link RequestModel}
 */
export const getReseller = createAction(
    '[Login Page] Get Reseller',
    props<{ request: RequestModel }>()
)

/**
 * Creates the action getResellerSuccess
 * that stores returned reseller on the
 * app state.
 * Receives a {@link ResellerModel}
 */
export const getResellerSuccess = createAction(
    '[Login Page] Get Reseller Success',
    props<{ id: string, name: string, shortName: string, logo: string, siteURL: string, favIcon: string }>()
)

/**
 * Creates the action login
 * that logs the user in the app.
 * Receives a {@link CredentialsModel}
 */
export const login = createAction(
    '[Login Page] Login',
    props<{ credentials: Credentials }>()
)

/**
 * Creates the action loginSuccess
 * that stores the logged user
 * on the app state.
 * Receives a {@link UserModel}
 */
export const loginSuccess = createAction(
    '[Login Page] Login Success',
    props<{ user: UserModel }>()
)

export const searchLocationAutocompleteOptions = createAction(
    '[Auth] Search Location Autocomplete Options',
    props<{ resellerId?: string }>()
)

export type LocationBusinessMetadata = {
    businessId: string,
    businessName: string,
    locationId: string,
    locationName: string,
}

export const searchLocationAutocompleteOptionsSuccess = createAction(
    '[Auth] Search Location Autocomplete Options Success',
    props<{ locationMetadata: { businessId: string, businessName: string, locations: LocationBusinessMetadata[] }[] }>()
)

/**
 * Creates the action loginFailure
 * that stores authentication
 * errors on the app state.
 * Receives a string.
 */
export const loginFailure = createAction(
    '[Login Page] Login Failure',
    props<{ error: string }>()
)

/**
 * Creates the action sendEmailPasswordRecover
 * that sends the user an email for recovering
 * the password .
 * Receives a string.
 */
export const sendEmailPasswordRecover = createAction(
    '[Login Page] Send Email Recover Password',
    props<{ email: string, resellerId: string }>()
)

/**
 * Creates the action sendEmailPasswordRecoverSuccess
 * that stores a variable that specifies if the recovery email was
 * successfully sent or not.
 * Receives a boolean.
 */
export const sendEmailPasswordRecoverSuccess = createAction(
    '[Login Page] Send Email Password Recover Success',
    props<{ sent: boolean }>()
)

/**
 * Creates the action confirmNewPassword
 * that restores user password via cognito.
 */
export const confirmNewPassword = createAction(
    '[Login Page] Confirm New Password',
    props<{ payload: { username: string, code: string, newPassword: string, resellerId: string } }>()
)

/**
 * Creates the action confirmNewPasswordSuccess
 * that stores a variable that indicates
 * if the password restore was 
 * successfull or not.
 * Receives a boolean.
 */
export const confirmNewPasswordSuccess = createAction(
    '[Login Page] Confirm New Password Success',
    props<{ pswChanged: boolean }>()
)

/**
 * Creates the action changePassword
 * that dispatches to change user password
 * via cognito.
 * Receives the old and the new password.
 */
export const changePassword = createAction(
    '[Login Page] Change Password',
    props<{ payload: { psw: string, newPsw: string } }>()
)

/**
 * Creates the action changePasswordSuccess
 * that stores a variable that indicates
 * if the password was successfully changed or not.
 * Receives a boolean.
 */
export const changePasswordSuccess = createAction(
    '[Login Page] Change Password Success'
)

/**
 * Creates the action validateAccount
 * that distpatches the flow
 * to validate user account via AWS cognito.
 */
export const validateAccount = createAction(
    '[Login Page] Validate Account',
    props<{ payload: { email: string, code: number } }>()
)

/**
 * Creates the action validateAccountSuccess
 * that stores if the user validation was
 * successfull or not.
 * Receives a boolean.
 */
export const validateAccountSuccess = createAction(
    '[Login Page] Validate Account Success',
    props<{ isValidate: boolean }>()
)

/**
 * Creates the action signUp
 * that dispatches cognito user sign up
 * flow.
 * Receives a {@link UserModel}.
 */
export const signUp = createAction(
    '[Login Page] sign Up',
    props<{ payload: UserModel }>()
)

/**
 * Creates the action signUpSuccess
 * that stores on the app the logged
 * user.
 */
export const signUpSuccess = createAction(
    '[Login Page] sign Up Success',
    props<{ data: any }>()
)

/**
 * Creates the action getSignUpUser
 * that searches on the data base 
 * for the user data to sign up.
 * Receives a string.
 */
export const getSignUpUser = createAction(
    '[Login Page] Get Sign up User',
    props<{ payload: { key: string } }>()
)

/**
 * Creates the action getSignUpUser
 * that stores the selected reseller
 * depending on the logged user on the app
 * state.
 * Receives a {@link ResellerModel}.
 */
export const selectReseller = createAction(
    '[Login Page] Select Reseller',
    props<{ payload: { id: string, name: string, shortName: string, logo: string, siteURL: string, favIcon: string } }>()
)

/**
 * Creates the action setPswChanged
 * that stores on the app state if the
 * password was successfully change or not.
 * Receives a booblean.
 */
export const setPswChanged = createAction(
    '[Login Page] Set Psw Changed',
    props<{ pswChanged: boolean }>()
)

/**
 * Creates the action setPswChanged
 * that dispatches cognito
 * flow to log out the user.
 */
export const logOut = createAction(
    '[Login Page] Log Out'
)

/**
 * Creates the action logOutSuccess
 * that stores if the user was succesfully
 * logged out or not on the app state.
 * Receives a boolean
 */
export const logOutSuccess = createAction(
    '[Login Page] Log Out Success',
    props<{ loggedOut: boolean }>()
)

/**
 * @ignore
 */
export const updateToken = createAction(
    '[Login Page] Update Token'
)

/**
 * Creates the action getLoggedUserData
 * that searches for the logged user data
 * on the data base.
 * Receives a {@link RequestModel}
 */
export const getLoggedUserData = createAction(
    '[Login Page] Get Logged User Data',
    props<{ request: RequestModel }>()
)

/**
 * Creates the action getLoggedUserDataSuccess
 * that stores the logged user data
 * on the app state.
 * Receives a {@link UserModel}
 */
export const getLoggedUserDataSuccess = createAction(
    '[Login Page] Get Logged User Data Success',
    props<{ loggedUser: UserModel }>()
)
//TODO Logout actions
